import { safeMedicalCertificateFileName } from "../../../utils/medicalCertificateFileName";
import React, { useState, useRef, useEffect } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { collection, doc, getDoc, getDocs, onSnapshot, runTransaction, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { v4 as uuid } from "uuid";
import { db, storage, functions } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { notifyError, notifyTopRight } from "../toast/SetToast";
import { getBookingReportChecklist, getSortedBookingReportFiles, isLphBookingNode } from "./BookingReportChecklistUtils";
import { getBookingDoctorPatientNoteFiles } from "./BookingPatientNoteChecklistUtils";
import { generateBookingMedicalCertBlob, getBookingMedicalCertTemplate, generateBookingDownloadCertBlob, getBookingDownloadCertTemplate, getBookingCertificateAge } from "./bookingMedicalCertificate";
import { extractAnnualMaskPdfText } from "../Followup/annualMaskClinicalPdfReader";
import { extractSleepStudyClinicalValues } from "../Followup/annualMaskClinicalDataUtils";

const escapeHtml = (value) => String(value || "").replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[character]));
const fileKey = (file) => `${file.id}/${file.fileName}`;
const readReports = async (hn) => (await getDocs(collection(db, "patients", hn, "report"))).docs.map((row) => ({ ...row.data(), id: row.id, hn }));
const readPatientNoteFiles = async (hn) => getBookingDoctorPatientNoteFiles(
  (await getDocs(collection(db, "patients", hn, "note"))).docs.map((row) => ({ ...row.data(), id: row.id, hn })), { hn },
);
const fileUrl = async (file, folder = "reports") => (await httpsCallable(functions, "getSignedUrl")({ folder, hn: file.hn, id: file.id, filename: file.fileName })).data;
const downloadCertificate = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  try { link.click(); }
  finally {
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};

export default function BookingDoctorReviewSection({ booking = {}, nodeId, reports = [], onReportsChange }) {
  const { currentUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [files, setFiles] = useState([]);
  const [patientNoteFiles, setPatientNoteFiles] = useState([]);
  const [selected, setSelected] = useState({});
  const [recipient, setRecipient] = useState("");
  const [isTest, setIsTest] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [emailReview, setEmailReview] = useState(null);
  const [testRequestId, setTestRequestId] = useState("");
  const [deliveryResult, setDeliveryResult] = useState(null);
  const [generatedCertificate, setGeneratedCertificate] = useState(null);
  const uploadRef = useRef(null);
  const props = booking.extendedProps || booking;
  const hn = String(props.hn || "").trim();
  const bookingId = props.uuid || booking.id;
  const roles = currentUser && Array.isArray(currentUser.roles) ? currentUser.roles : [];
  const canSend = roles.some((role) => ["admin", "super_admin"].includes(role));
  const isLph = isLphBookingNode(props.node);
  const canEmail = canSend && isLph;
  const canTest = canEmail && String((currentUser && currentUser.email) || "").trim().toLowerCase() === "admin888@mail.com";
  const canUpload = canSend || (roles.includes("sleep_physician") && props.doctor === currentUser.displayName);
  const checklist = getBookingReportChecklist(reports, props);
  const hasSleepStudyReport = checklist.parts.some((part) => part.key === "sleepStudyReport" && part.files.length > 0);
  let downloadUnavailable = "";
  try { getBookingDownloadCertTemplate(props.node, props.doctor, props.order); }
  catch (error) { downloadUnavailable = error.message; }
  useEffect(() => {
    setEmailReview(null);
    setTestRequestId("");
    if (!isLphBookingNode(props.node) || !nodeId || !bookingId) return undefined;
    return onSnapshot(doc(db, "nodes", nodeId, "bookings", bookingId), (snapshot) => {
      const saved = snapshot.exists() ? snapshot.data().extendedProps || {} : {};
      setEmailReview(saved.bookingDoctorReview || {});
    }, () => notifyError("Unable to refresh doctor email status. Reopen the booking to try again."));
  }, [nodeId, bookingId, props.node]);
  const trackedRequestId = testRequestId || (emailReview || props.bookingDoctorReview || {}).requestId;
  useEffect(() => {
    setDeliveryResult(null);
    if (!canSend || !trackedRequestId) return undefined;
    return onSnapshot(doc(db, "annualMaskOutboundMail", trackedRequestId), (snapshot) => {
      setDeliveryResult(snapshot.exists() ? snapshot.data() : null);
    }, () => setDeliveryResult(null));
  }, [canSend, trackedRequestId]);
  if (!isLph && (!canSend || downloadUnavailable)) return null;
  const review = emailReview || props.bookingDoctorReview || {};
  const emailState = review.state || "not_sent";
  const emailLabel = { not_sent: "Not sent", queued: "Queued", processing: "Sending", sent: "Sent", failed: "Failed", delivery_unknown: "Delivery unconfirmed" }[emailState] || "Unknown";
  const emailTime = emailState === "sent" ? review.sentAt : review.queuedAt;
  const emailDate = emailTime && (typeof emailTime.toDate === "function" ? emailTime.toDate() : new Date(emailTime));
  const emailDateLabel = emailDate && !Number.isNaN(emailDate.getTime()) ? emailDate.toLocaleString("en-GB", { timeZone: "Asia/Bangkok", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  const hasCertificate = checklist.parts.find((part) => part.key === "medicalCertificate").files.length > 0;
  const generated = generatedCertificate || review.generatedCertificate;
  const certificateFilename = `${safeMedicalCertificateFileName(props.order || hn)}_medical_certificate_unsigned.docx`;
  const downloadDraft = async () => {
    setBusy(true);
    try {
      const response = await fetch(await getDownloadURL(ref(storage, generated.storagePath)));
      if (!response.ok) throw new Error("Cannot download the certificate draft.");
      downloadCertificate(await response.blob(), generated.filename || "medical_certificate_unsigned.docx");
    } catch (error) { notifyError(error.message); }
    finally { setBusy(false); }
  };
  const bookingRef = () => {
    if (!hn || !nodeId || !bookingId) throw new Error("Save the booking with its HN and node first.");
    return doc(db, "nodes", nodeId, "bookings", bookingId);
  };
  const checkIdentity = (snapshot) => {
    if (!snapshot.exists()) throw new Error("The booking no longer exists.");
    const latest = snapshot.data().extendedProps || {};
    if (latest.hn !== props.hn || latest.doctor !== props.doctor || latest.node !== props.node) throw new Error("The booking changed. Reopen it before continuing.");
  };
  const prepare = async (testMode = false, downloadOnly = false) => {
    if (downloadOnly ? !canSend : testMode ? !canTest : !canEmail) {
      notifyError("You do not have permission to send this doctor email.");
      return;
    }
    setIsTest(testMode);
    setManualMode(downloadOnly);
    setBusy(true);
    try {
      if (downloadOnly) getBookingDownloadCertTemplate(props.node, props.doctor, props.order);
      else getBookingMedicalCertTemplate(props.node, props.doctor);
      const [saved, patientDoc, freshReports, doctors, noteFiles] = await Promise.all([
        getDoc(bookingRef()), getDoc(doc(db, "patients", hn)), readReports(hn), getDocs(collection(db, "doctors")),
        downloadOnly ? Promise.resolve([]) : readPatientNoteFiles(hn),
      ]);
      checkIdentity(saved);
      const caseReports = freshReports.filter((report) => !report.bookingUploadBookingPath || report.bookingUploadBookingPath === bookingRef().path);
      const fresh = getBookingReportChecklist(caseReports, props);
      if (downloadOnly) {
        if (!fresh.parts.some((part) => part.key === "sleepStudyReport" && part.files.length > 0)) throw new Error("Upload a Sleep Study Report before generating the certificate.");
      } else if (!fresh.isReady) throw new Error(`Waiting for: ${fresh.missingParts.map((part) => part.label).join(", ")}`);
      const doctor = doctors.docs.find((row) => [row.id, row.data().name].some((value) => String(value || "").replace(/\s/g, "").toLowerCase() === String(props.doctor || "").replace(/\s/g, "").toLowerCase()));
      const email = doctor && doctor.data().email;
      const patient = patientDoc.exists() ? patientDoc.data() : {};
      const reportFiles = getSortedBookingReportFiles(caseReports, props);
      setFiles(reportFiles);
      setPatientNoteFiles(noteFiles);
      setSelected(fresh.parts.filter((part) => part.required).reduce((values, part) => ({ ...values, [part.key]: part.files.length === 1 ? fileKey(part.files[0]) : "" }), {}));
      setRecipient(email || "");
      setDraft({ node: props.node, doctor: props.doctor, hn, order: props.order || "",
        hospitalNumber: String((saved.data().extendedProps || {}).hospitalNumber || patient.refHn || patient.hospitalNumber || "").trim(),
        patientName: [patient.firstnameTh || props.firstname, patient.lastnameTh || props.lastname].filter(Boolean).join(" "),
        studyDate: String(props.latestSleepStudyDate || "").slice(0, 10), ahi: props.ahi === undefined ? "" : String(props.ahi),
        dateOfBirth: String(patient.dateOfBirth || "").slice(0, 10),
        minSpo2: "", gender: props.gender || patient.gender || "", age: patient.age || getBookingCertificateAge(patient.dateOfBirth), height: patient.height || "",
        weight: patient.weight || "", bmi: patient.bmi || "",
      });
    } catch (error) { notifyError(error.message); }
    finally { setBusy(false); }
  };
  const readClinical = async () => {
    setBusy(true);
    try {
      const file = files.find((item) => fileKey(item) === selected.sleepStudyReport);
      if (!file || !/\.pdf$/i.test(file.fileName)) throw new Error("Select a Sleep Study Report PDF to read its values.");
      const response = await fetch(await fileUrl(file));
      if (!response.ok) throw new Error("Cannot read the Sleep Study Report.");
      const values = extractSleepStudyClinicalValues(await extractAnnualMaskPdfText(await response.blob()));
      setDraft((current) => ({ ...current,
        ahi: values.ahi || current.ahi, minSpo2: values.minSpo2 || current.minSpo2,
        studyDate: values.latestSleepStudyDate || current.studyDate,
        age: values.age || current.age, weight: values.weight || current.weight,
        height: values.height || current.height, bmi: values.bmi || current.bmi,
      }));
    } catch (error) { notifyError(error.message); }
    finally { setBusy(false); }
  };
  const send = async () => {
    if (manualMode || (isTest ? !canTest : !canEmail)) {
      notifyError("You do not have permission to send this doctor email.");
      return;
    }
    if (!recipient) {
      notifyError("The assigned doctor has no email address.");
      return;
    }
    setBusy(true);
    let generatedRef;
    let requestStarted = false;
    try {
      const [saved, noteFiles] = await Promise.all([getDoc(bookingRef()), readPatientNoteFiles(hn)]);
      checkIdentity(saved);
      const certificate = await generateBookingMedicalCertBlob(draft);
      const requestId = uuid();
      const filename = certificateFilename;
      generatedRef = ref(storage, `booking-medical-certificates/${hn}/${bookingId}/${requestId}/${filename}`);
      await uploadBytes(generatedRef, certificate, { contentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const certificateUrl = await getDownloadURL(generatedRef);
      const attachments = [];
      for (const part of checklist.parts.filter((item) => item.required)) {
        const file = files.find((item) => fileKey(item) === selected[part.key]);
        if (!file) throw new Error(`Select ${part.label}.`);
        attachments.push({ filename: file.fileName, path: await fileUrl(file) });
      }
      attachments.push({ filename, path: certificateUrl });
      for (const file of noteFiles) {
        attachments.push({ filename: file.fileName, path: await fileUrl(file, "files") });
      }
      requestStarted = true;
      const queued = await httpsCallable(functions, "queueBookingDoctorEmail")({
        casePath: bookingRef().path, requestId, isTest,
        message: {
          subject: `LPH Sleep Study - ${props.order || draft.hospitalNumber || draft.patientName} - Medical certificate for review`,
          text: `Dear ${props.doctor},\n\nPlease review the attached reports and medical certificate for the patient below. You can adjust the pressure settings as needed before signing.\n\nPatient: ${draft.patientName}\nHospital number: ${draft.hospitalNumber || "Not recorded"}\nSleep study order: ${props.order || "-"}\n\nOnce signed, please save the certificate as a PDF and upload it to the patient’s booking.\n\nOpen Booking Calendar: ${["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname) ? "https://study.bdsleep.tech" : window.location.origin}/booking-calendar\nSearch for the patient by name: ${draft.patientName}\n\nThank you,\nBDSleep`,
          html: `
            <div style="font-family:Arial,sans-serif;font-size:15px;line-height:1.7;color:#253047;max-width:600px;margin:0;padding:16px 0;text-align:left;">
              <p style="margin:0 0 20px;">Dear ${escapeHtml(props.doctor)},</p>
              <p style="margin:0 0 20px;">Please review the attached reports and medical certificate for the patient below. You can adjust the pressure settings as needed before signing.</p>
              <div style="background:#f5f7fa;border-left:3px solid #315b81;padding:14px 18px;margin:0 0 20px;">
                <strong>Patient:</strong> ${escapeHtml(draft.patientName)}<br>
                <strong>Hospital number:</strong> ${escapeHtml(draft.hospitalNumber || "Not recorded")}<br>
                <strong>Sleep study order:</strong> ${escapeHtml(props.order || "-")}
              </div>
              <p style="margin:0 0 20px;">Once signed, please save the certificate as a PDF and upload it to the patient’s booking.</p>
              <p style="margin:0 0 8px;"><a style="color:#245b8a;font-weight:bold;" href="${escapeHtml(["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname) ? "https://study.bdsleep.tech" : window.location.origin)}/booking-calendar">Open Booking Calendar</a></p>
              <p style="margin:0 0 24px;">Search for the patient by name:<br><strong>${escapeHtml(draft.patientName)}</strong></p>
              <p style="margin:0;">Thank you,<br>BDSleep</p>
            </div>
          `,
          attachments,
        },
      });
      setTestRequestId(isTest && queued.data && queued.data.requestId ? queued.data.requestId : "");
      if (!isTest) {

        setGeneratedCertificate({ filename, storagePath: generatedRef.fullPath });
      }
      setDraft(null);
      notifyTopRight(isTest ? "Test email queued to pisanu@braindynamics.co.th." : "Doctor email queued with the reports, unsigned medical certificate, and available patient forms.");
    } catch (error) {
      // Keep attachments if a timed-out request may already have queued the email.
      if (generatedRef && !requestStarted) await deleteObject(generatedRef).catch(() => {});
      notifyError(error.message);
    } finally { setBusy(false); }
  };
  const generateAndDownload = async () => {
    if (busy || !draft) return;
    if (!canSend) {
      notifyError("You do not have permission to generate this medical certificate.");
      return;
    }
    setBusy(true);
    try {
      const [saved, freshReports] = await Promise.all([getDoc(bookingRef()), readReports(hn)]);
      checkIdentity(saved);
      const caseReports = freshReports.filter((report) => !report.bookingUploadBookingPath || report.bookingUploadBookingPath === bookingRef().path);
      if (!getBookingReportChecklist(caseReports, props).parts.some((part) => part.key === "sleepStudyReport" && part.files.length > 0)) {
        throw new Error("Upload a Sleep Study Report before generating the certificate.");
      }
      const certificate = await generateBookingDownloadCertBlob(draft);
      downloadCertificate(certificate, certificateFilename);
      notifyTopRight("Medical certificate downloaded for manual sending.");
    } catch (error) { notifyError(error.message); }
    finally { setBusy(false); }
  };
  const uploadSigned = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    let target;
    let saveStarted = false;
    try {
      if (!canUpload || !/\.pdf$/i.test(file.name)) throw new Error("Upload the doctor's signed medical certificate as a PDF.");
      const reportRows = await readReports(hn);
      if (!getBookingReportChecklist(reportRows, props).isReady) throw new Error("The required reports must be uploaded first.");
      const reportRef = doc(collection(db, "patients", hn, "report"));
      const filename = `${safeMedicalCertificateFileName(props.order || hn)}_medical_certificate_signed_${safeMedicalCertificateFileName(file.name)}`;
      target = ref(storage, `reports/${hn}/${reportRef.id}/${filename}`);
      await uploadBytes(target, file, { contentType: "application/pdf" });
      saveStarted = true;
      await runTransaction(db, async (transaction) => {
        checkIdentity(await transaction.get(bookingRef()));
        transaction.set(reportRef, { hn, reportFile: [filename], createBy: currentUser.displayName || currentUser.email,
          createAt: serverTimestamp(), bookingUploadBookingPath: bookingRef().path,
        });
      });
      const rows = await readReports(hn);
      if (onReportsChange) onReportsChange(rows);
      notifyTopRight("Signed medical certificate uploaded.");
    } catch (error) {
      if (target && !saveStarted) await deleteObject(target).catch(() => {});
      notifyError(error.message);
    } finally { setBusy(false); }
  };
  return <div className="border rounded p-2 my-2">
    <div className="d-flex align-items-start flex-wrap mb-3" style={{ gap: 16 }}>
      <div style={{ flex: "1 1 280px", minWidth: 0 }}>
        <div className="small mb-2"><b>{isLph ? "LPH Doctor Review:" : "Medical Certificate:"}</b> {hasCertificate ? "Medical certificate uploaded" : hasSleepStudyReport ? "Sleep Study Report ready for certificate download" : "Waiting for Sleep Study Report"}</div>
        {isLph && <div className="d-flex align-items-center flex-wrap small" style={{ gap: 8 }}>
          <b>Doctor Email Status:</b>
          <span className={`badge ${emailState === "sent" ? "badge-success" : emailState === "failed" || emailState === "delivery_unknown" ? "badge-warning" : "badge-secondary"}`}>{emailLabel}</span>
          {emailDateLabel && <span className="text-muted">{emailState === "sent" ? "Sent" : "Queued"}: {emailDateLabel} (Bangkok)</span>}
          {review.doctor && <span className="text-muted">Doctor: {review.doctor}</span>}
        </div>}
      </div>
      {((canSend && !downloadUnavailable) || canTest) && (
        <div className="d-flex flex-column ml-auto" style={{ gap: 8 }}>
          {canSend && !downloadUnavailable && <Button size="sm" variant="outline-primary" title="Generate a certificate for manual sending. Requires a Sleep Study Report." disabled={busy || !hasSleepStudyReport} onClick={() => prepare(false, true)}>Generate Medical Cert</Button>}
          {canTest && <Button size="sm" variant="outline-secondary" disabled={busy || !checklist.isReady} onClick={() => prepare(true)}>Test Send to Doctor</Button>}
        </div>
      )}
    </div>
    <div className="d-flex align-items-center flex-wrap" style={{ columnGap: 32, rowGap: 16 }}>
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
        {canEmail && <Button size="sm" disabled={busy || !checklist.isReady} onClick={() => prepare(false)}>{review.requestId ? "Send to Doctor Again" : "Send to Doctor"}</Button>}
        {isLph && canUpload && generated && <Button size="sm" variant="outline-secondary" disabled={busy} onClick={downloadDraft}>Download certificate to sign</Button>}
        {isLph && canUpload && <Button size="sm" variant="outline-primary" disabled={busy || !checklist.isReady} onClick={() => uploadRef.current.click()}>Upload signed medical certificate</Button>}
      </div>
    </div>
    {canSend && testRequestId && deliveryResult && <div className="small mt-2"><b>Test Email Status:</b> {({ queued: "Queued", processing: "Preparing attachments and sending", sent: "Sent", failed: "Failed", delivery_unknown: "Delivery unconfirmed" })[deliveryResult.state] || deliveryResult.state}</div>}
    {canSend && deliveryResult && deliveryResult.error && <div className="small text-danger mt-2" role="alert">{deliveryResult.error}</div>}
    <input ref={uploadRef} type="file" accept="application/pdf,.pdf" hidden onChange={uploadSigned} />
    <Modal show={Boolean(draft)} onHide={() => !busy && setDraft(null)} size="lg" backdrop="static">
      <Modal.Header closeButton={!busy}><Modal.Title>{manualMode ? `${props.node} — Medical Certificate` : isTest ? "LPH — Test Send to Doctor" : "LPH — Send to Doctor"}</Modal.Title></Modal.Header>
      <Modal.Body>{draft && <>
        <p className="small">Doctor: <b>{props.doctor}</b> ({recipient || "No email address"}). Confirm these values against the Sleep Study Report before generating the certificate.</p>
        <p className="small text-muted">Download the unsigned DOCX to send to the doctor manually. Downloading does not send email or change the email status.</p>
        {isTest && <p className="small text-info">Test sender and recipient: <b>pisanu@braindynamics.co.th</b>. The certificate and email wording use the assigned doctor. This test does not mark the case as sent to the doctor.</p>}
        {checklist.parts.filter((part) => manualMode ? part.key === "sleepStudyReport" : part.required).map((part) => <Form.Group key={part.key}><Form.Label>{part.label}</Form.Label><Form.Control as="select" disabled={busy} value={selected[part.key] || ""} onChange={(event) => setSelected({ ...selected, [part.key]: event.target.value })}><option value="">Select report for this case</option>{getBookingReportChecklist(files.map((file) => ({ id: file.id, hn: file.hn, reportFile: [file.fileName] })), props).parts.find((item) => item.key === part.key).files.map((file) => <option key={fileKey(file)} value={fileKey(file)}>{file.fileName}</option>)}</Form.Control></Form.Group>)}
        {!manualMode && <div className="small mb-3">
          <b>Optional patient files:</b> OPD Card and Sleep Study Form files are attached automatically when available.
          <div className="text-muted">For connected email senders, all OPD Card and form image copies are reduced automatically to fit alongside the PDFs. Originals are kept. If the total still exceeds 18 MB, the send status lists the files to reduce.</div>
          {patientNoteFiles.length > 0 ? <ul className="mb-0 mt-1">{patientNoteFiles.map((file) => <li key={fileKey(file)}>{file.title}: {file.fileName}</li>)}</ul> : <div className="text-muted">No OPD Card or Sleep Study Form files uploaded. You can still send this email.</div>}
        </div>}
        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={readClinical}>Read values from selected PDF</Button>
        <div className="row mt-2">{(isLph ? [["patientName", "Patient name"], ["studyDate", "Study date"], ["ahi", "AHI"], ["minSpo2", "Minimum SpO2 (%)"], ["gender", "Gender"], ["age", "Age at study"], ["height", "Height (cm)"], ["weight", "Weight (kg)"], ["bmi", "BMI"]] : [["patientName", "Patient name"], ["studyDate", "Study date"], ["hospitalNumber", "Hospital number"], ["dateOfBirth", "Date of birth"], ["gender", "Gender"]]).map(([key, label]) => <Form.Group className="col-md-6" key={key}><Form.Label>{label}</Form.Label><Form.Control disabled={busy} type={["studyDate", "dateOfBirth"].includes(key) ? "date" : "text"} value={draft[key]} onChange={(event) => setDraft({ ...draft, [key]: event.target.value })} /></Form.Group>)}</div>
      </>}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={() => setDraft(null)}>Cancel</Button>
        <Button variant="outline-primary" disabled={busy || !canSend} onClick={generateAndDownload}>Generate and Download DOCX</Button>
        {!manualMode && <Button disabled={busy || !recipient || (isTest ? !canTest : !canEmail)} onClick={send}>{busy ? "Preparing..." : isTest ? "Generate Certificate and Send Test" : "Generate Certificate and Send to Doctor"}</Button>}
      </Modal.Footer>
    </Modal>
  </div>;
}
